import React from 'react';

import uid from '../../../../utils/uid';

export interface UploadFile extends File {
    uid?: string;
}

export interface UploadChangeEvent {
    file: UploadFile;
    fileList: UploadFile[];
}

export interface ImplicitFileInputProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        'type' | 'onChange' | 'value'
    > {
    onChange?: (info: UploadChangeEvent) => void;

    beforeUpload?: (
        file: UploadFile,
        fileList: UploadFile[]
    ) =>
        | boolean
        | Promise<UploadFile | void>
        | UploadFile
        | void;

    accept?: string;
    files?: UploadFile[];
    multiple?: boolean;
}

const ImplicitFileInput: React.FC<ImplicitFileInputProps> = ({
    onChange = () => {},
    beforeUpload = () => {},
    accept = '*',
    files = [],
    multiple = false,
    ...props
}) => {
    const handleChange = ({
        file,
        fileList,
    }: UploadChangeEvent) => {
        onChange?.({
            file,
            fileList: [...files, ...fileList],
        });
    };

    const upload = (
        file: UploadFile,
        fileList: UploadFile[]
    ) => {
        if (!beforeUpload) {
            setTimeout(() => {
                onChange?.({ file, fileList });
            }, 0);

            return;
        }

        const before = beforeUpload(file, fileList);

        if (
            before &&
            typeof (before as any).then === 'function'
        ) {
            (before as Promise<any>)
                .then((processedFile) => {
                    const type =
                        Object.prototype.toString.call(
                            processedFile
                        );

                    if (
                        type === '[object File]' ||
                        type === '[object Blob]'
                    ) {
                        onChange?.({ file, fileList });
                        return;
                    }

                    handleChange({ file, fileList });
                })
                .catch((e) => {
                    console.log(e);
                });
        } else if (before !== false) {
            setTimeout(() => {
                handleChange({ file, fileList });
            }, 0);
        } else {
            handleChange({ file, fileList });
        }
    };

    const uploadFiles = (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const inputFiles = e.target.files;

        if (!inputFiles) return;

        const fileList: UploadFile[] =
            Array.from(inputFiles);

        fileList
            .map((file) => {
                file.uid = uid();
                return file;
            })
            .forEach((file) => {
                upload(file, fileList);
            });
    };

    const handleClick = (
        e: React.MouseEvent<HTMLInputElement>
    ) => {
        // allow re-selecting same file
        e.currentTarget.value = '';
    };

    return (
        <input
            {...props}
            onChange={uploadFiles}
            onClick={handleClick}
            multiple={multiple}
            type="file"
            accept={accept}
        />
    );
};

export default ImplicitFileInput;
